import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Icon1 from "../icons/Icon.js";
import Spinner from "../loading/Spinner.js";
import SmallSpinner from "../loading/SmallSpinner.js";
import CheckBoxGrey from "../textfields/CheckBoxGrey.js";
import { File } from "../types/types.js";

interface Dropzone3Props {
  onDrop: (files: File[]) => void;
  files?: File[];
  maxFiles?: number;
  maxSize?: number;
  dropzoneStyles?: string;
  instructionsText?: string;
  onDelete: (file: File) => void;
  loading?: boolean;
  mandatory?: boolean;
  only_zip?: boolean;
  handleClickVariant: (
    message: any,
    type: "success" | "error" | "default" | "warning" | "info" | undefined
  ) => void;
  selected_files?: File[];
  setSelectedDroppedFiles: (files: any) => void;
}

const Dropzone3: React.FC<Dropzone3Props> = ({
  onDrop,
  files = [],
  maxFiles = Infinity,
  maxSize = 52428800,
  dropzoneStyles = "",
  instructionsText = "Glissez vos fichiers ici, ou cliquez pour sélectionner des fichiers",
  onDelete,
  loading,
  mandatory = false,
  only_zip = false,
  handleClickVariant,
  selected_files,
  setSelectedDroppedFiles,
}) => {
  const [loadingData, setLoadingData] = React.useState(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles: any) => {
      setLoadingData(true);

      let newFiles = [];
      for (const file of acceptedFiles) {
        if (
          ![
            "text/xml",
            "application/x-zip-compressed",
            "application/zip",
            "application/xml",
          ].includes(file.type)
        ) {
          if (only_zip) {
            console.error(file.type);
            handleClickVariant(
              "Veuillez déposer un fichier au bon format",
              "error"
            );
            newFiles = [];
            break;
          } else {
            newFiles.push(file); // Add non-ZIP files directly
          }
        } else {
          newFiles.push(file); // Add non-ZIP files directly
        }
      }

      if (newFiles.length > 0) {
        onDrop([...files, ...newFiles]); // Append new files to existing ones
      }

      setLoadingData(false);
    },
    [onDrop, files]
  );

  const onDropRejected = useCallback(
    (rejectedFiles: any) => {
      rejectedFiles.forEach((file: any) => {
        if (file.file.size > maxSize) {
          console.error(
            `Rejected file due to size: ${file.file.name}. Size: ${file.file.size} bytes. Max allowed size: ${maxSize} bytes.`
          );
        } else {
          console.warn(
            `Rejected file: ${file.file.name}, Type: ${file.file.type}`
          );
        }
      });
      const fileNames = rejectedFiles.map((file: any) => file.file.name);
      alert(`Invalid file types or sizes: ${fileNames.join(", ")}`);
    },
    [maxSize]
  );

  const [onHover, setOnHover] = React.useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    onDropRejected,
    accept: {
      "application/pdf": [],
      "application/xml": [],
      "application/zip": [],
      "application/x-zip-compressed": [],
      "text/xml": [],
    },
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div className="flex flex-col items-center w-full  py-1  gap-4 ">
      <div
        {...getRootProps()}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
        className={` rounded-2xl p-4 w-full h-c250 flex items-center justify-center text-center cursor-pointer ${
          mandatory
            ? "border-red-500 bg-red-100"
            : isDragActive
            ? "border-blue-500 bg-neutral-200"
            : onHover
            ? "bg-neutral-300 shadow-inner border-shadow_25"
            : "border-primary bg-neutral-200"
        } ${dropzoneStyles}`}
      >
        <input {...getInputProps()} />
        {loadingData ? (
          <SmallSpinner loading={true} />
        ) : (
          <div className="flex flex-col items-center">
            {isDragActive ? (
              <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                <Icon1 icon_name="join" size={50} fill="grey" />
                <div className="text-hx font-semibold text-gray-500 w-c200">
                  Lâchez pour déposer
                </div>
              </div>
            ) : (
              (!loading && (
                <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                  <Icon1 icon_name="upload" size={50} fill="grey" />
                  <div className="text-hx font-semibold text-gray-500 w-c200">
                    Glissez et déposez vos fichiers ici
                  </div>
                  <div className="text-h3  text-shadow_50 w-c200">
                    Ou <span className="underline">cliquez</span> pour
                    sélectionner des fichiers
                  </div>
                </div>
              )) ||
              (loading && (
                <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                  <Icon1
                    icon_name="file"
                    size={50}
                    fill="rgba(80, 81, 255, 0.7)"
                  />
                  <div className="text-hx2 font-bold text-black w-c200">
                    Téléchargement en cours ...
                  </div>
                  <SmallSpinner loading={true} margin={0} size={30} />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Display the dropped files */}
      <div className="w-full rounded-lg flex flex-col h-fit  py-1 gap-2 overflow-y-auto max-h-c300 ">
        {files.length !== 0 ? (
          files.map((file, index) => (
            <div
              key={index}
              className="w-full flex-row flex justify-between items-center "
            >
              <div className="flex flex-row gap-2 items-center bg-blue-50 rounded-lg px-2 py-1 w-full">
                {selected_files && (
                  <CheckBoxGrey
                    checked={selected_files?.includes(file)}
                    onChange={() => {
                      if (selected_files.includes(file)) {
                        setSelectedDroppedFiles(
                          selected_files.filter((f) => f !== file)
                        );
                      } else {
                        setSelectedDroppedFiles([...selected_files, file]);
                      }
                    }}
                  ></CheckBoxGrey>
                )}
                <Icon1 icon_name="file" size={20} fill="grey" />
                <div className="flex flex-col ">
                  <div className="text-h3 text-shadow_50">{file.name}</div>
                </div>
                <div
                  className="cursor-pointer ml-auto"
                  onClick={() => onDelete(file)}
                >
                  <Icon1
                    icon_name="delete"
                    size={18}
                    fill="grey"
                    hover_color="red"
                  />
                </div>{" "}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-h2 text-shadow_50 h-full w-full min-h-10 justify-center flex flex-col">
            Pas de document déposé
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropzone3;
